import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FormModal } from './FormModal';
import { RichTextEditor } from './RichTextEditor';
import { ClauseVersionModal, ClauseVersionFormData } from './ClauseVersionModal';
import * as api from '../services/apiClient';
import { logger } from '../utils/logger';
import { cleanEmptyHtml } from '../shared/htmlUtils';
import type { ClauseVersion } from '../shared/playbook-types';
import { useImpersonation } from '../context/ImpersonationContext';

// Temporary version for local state management
interface TempClauseVersion {
  tempId: string;
  id?: number;
  name: string;
  content: string;
  selection_criteria: string;
  isNew?: boolean;
  isDeleted?: boolean;
  isModified?: boolean;
}

export interface ClauseTypeFormData {
  name: string;
  acceptable_terms: string;
  unacceptable_terms: string;
}

export interface ClauseTypeResult extends ClauseTypeFormData {
  id: number;
}

interface ClauseTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  initialData?: ClauseTypeFormData;
  clauseId?: number;
  initialVersions?: ClauseVersion[];
  onSubmitSuccess?: (data: ClauseTypeResult) => void;
  onRefreshNeeded?: () => void;
}

const defaultFormData: ClauseTypeFormData = {
  name: '',
  acceptable_terms: '',
  unacceptable_terms: '',
};

export const ClauseTypeModal: React.FC<ClauseTypeModalProps> = ({
  isOpen,
  onClose,
  mode,
  initialData,
  clauseId,
  initialVersions,
  onSubmitSuccess,
  onRefreshNeeded,
}) => {
  const { activeAlias } = useImpersonation();
  const previousAliasRef = useRef(activeAlias);
  const [formData, setFormData] = useState<ClauseTypeFormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close modal if impersonation changes while open
  useEffect(() => {
    if (isOpen && previousAliasRef.current !== activeAlias) {
      onClose();
    }
    previousAliasRef.current = activeAlias;
  }, [activeAlias, isOpen, onClose]);

  // Version management state
  const [versions, setVersions] = useState<TempClauseVersion[]>([]);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [versionModalMode, setVersionModalMode] = useState<'add' | 'edit'>('add');
  const [editingVersion, setEditingVersion] = useState<TempClauseVersion | null>(null);

  // Computed: active versions (not deleted)
  const activeVersions = versions.filter(v => !v.isDeleted);

  // Reset form when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setFormData(initialData);
      } else {
        setFormData(defaultFormData);
      }
      // Load versions
      if (mode === 'edit' && initialVersions) {
        setVersions(initialVersions.map(v => ({
          tempId: `existing-${v.id}`,
          id: v.id,
          name: v.name,
          content: v.content,
          selection_criteria: v.selection_criteria,
          isNew: false,
          isDeleted: false,
          isModified: false,
        })));
      } else {
        setVersions([]);
      }
      setError(null);
      setVersionModalOpen(false);
      setEditingVersion(null);
    }
  }, [isOpen, mode, initialData, initialVersions]);

  const updateField = useCallback(<K extends keyof ClauseTypeFormData>(
    field: K,
    value: ClauseTypeFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Version handlers
  const handleAddVersion = useCallback(() => {
    setVersionModalMode('add');
    setEditingVersion(null);
    setVersionModalOpen(true);
  }, []);

  const handleEditVersion = useCallback((version: TempClauseVersion) => {
    setVersionModalMode('edit');
    setEditingVersion(version);
    setVersionModalOpen(true);
  }, []);

  const handleDeleteVersion = useCallback((tempId: string) => {
    setVersions(prev => prev.map(v =>
      v.tempId === tempId ? { ...v, isDeleted: true } : v
    ));
  }, []);

  const handleVersionModalClose = useCallback(() => {
    setVersionModalOpen(false);
    setEditingVersion(null);
  }, []);

  const handleVersionSaveLocal = useCallback((data: ClauseVersionFormData) => {
    if (versionModalMode === 'add') {
      // Adding new version
      const newVersion: TempClauseVersion = {
        tempId: `temp-${Date.now()}`,
        name: data.name,
        content: data.content,
        selection_criteria: data.selection_criteria,
        isNew: true,
        isDeleted: false,
        isModified: false,
      };
      setVersions(prev => [...prev, newVersion]);
    } else if (editingVersion) {
      // Editing existing version
      setVersions(prev => prev.map(v =>
        v.tempId === editingVersion.tempId
          ? { ...v, ...data, isModified: !v.isNew }
          : v
      ));
    }
  }, [versionModalMode, editingVersion]);

  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      setError('Le nom de la clause est requis');
      return;
    }

    // Validate at least one version
    if (activeVersions.length === 0) {
      setError('Vous devez ajouter au moins une version de clause');
      return;
    }

    // Validate clauseId for edit mode
    if (mode === 'edit') {
      if (clauseId === null || clauseId === undefined || !Number.isFinite(clauseId)) {
        setError('Erreur: ID de clause invalide pour la modification');
        logger.error('Edit mode attempted without valid clauseId:', clauseId);
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let response;

      if (mode === 'edit') {
        // clauseId is guaranteed to be valid here due to the check above
        response = await api.updateClause(clauseId!, {
          name: formData.name,
          acceptable_terms: cleanEmptyHtml(formData.acceptable_terms),
          unacceptable_terms: cleanEmptyHtml(formData.unacceptable_terms),
        });
      } else {
        response = await api.createClause({
          name: formData.name,
          acceptable_terms: cleanEmptyHtml(formData.acceptable_terms),
          unacceptable_terms: cleanEmptyHtml(formData.unacceptable_terms),
        });
      }

      if (response.success && response.data) {
        const finalClauseId = response.data.id;

        // Process versions with error collection
        const versionErrors: string[] = [];
        for (const version of versions) {
          try {
            if (version.isDeleted && version.id) {
              // Delete existing versions marked for deletion
              const deleteResult = await api.deleteClauseVersion(version.id);
              if (deleteResult.success) {
                logger.info(`Version supprimée: ${version.id} pour clause ${finalClauseId}`);
              } else {
                const errorMsg = `Échec suppression version ${version.id} (${version.name}): ${deleteResult.error}`;
                logger.error(errorMsg, { finalClauseId, versionId: version.id, versionName: version.name });
                versionErrors.push(errorMsg);
              }
            } else if (version.isNew && !version.isDeleted) {
              // Create new versions
              const createResult = await api.createClauseVersion({
                clause_type_id: finalClauseId,
                name: version.name,
                content: cleanEmptyHtml(version.content) || undefined,
                selection_criteria: cleanEmptyHtml(version.selection_criteria) || undefined,
              });
              if (createResult.success) {
                logger.info(`Version créée pour clause ${finalClauseId}: ${version.name}`);
              } else {
                const errorMsg = `Échec création version "${version.name}": ${createResult.error}`;
                logger.error(errorMsg, { finalClauseId, versionName: version.name });
                versionErrors.push(errorMsg);
              }
            } else if (version.id && version.isModified && !version.isDeleted) {
              // Update modified existing versions
              const updateResult = await api.updateClauseVersion(version.id, {
                name: version.name,
                content: cleanEmptyHtml(version.content),
                selection_criteria: cleanEmptyHtml(version.selection_criteria),
              });
              if (updateResult.success) {
                logger.info(`Version modifiée: ${version.id} pour clause ${finalClauseId}`);
              } else {
                const errorMsg = `Échec modification version ${version.id} (${version.name}): ${updateResult.error}`;
                logger.error(errorMsg, { finalClauseId, versionId: version.id, versionName: version.name });
                versionErrors.push(errorMsg);
              }
            }
          } catch (versionErr) {
            const errorMsg = `Erreur inattendue pour version ${version.id || 'nouvelle'} (${version.name}): ${versionErr instanceof Error ? versionErr.message : 'Erreur inconnue'}`;
            logger.error(errorMsg, { finalClauseId, versionId: version.id, versionName: version.name, error: versionErr });
            versionErrors.push(errorMsg);
          }
        }

        // If there were version errors, show them and keep modal open
        if (versionErrors.length > 0) {
          setError(`La clause a été sauvegardée mais des erreurs sont survenues pour les versions:\n${versionErrors.join('\n')}`);
          logger.error(`Clause ${finalClauseId} sauvegardée avec ${versionErrors.length} erreur(s) de version`, { finalClauseId, errors: versionErrors });
          // Still call refresh since clause was saved
          onRefreshNeeded?.();
          return;
        }

        logger.info(`Clause ${mode === 'edit' ? 'modifiée' : 'créée'}: ${response.data.id}`);
        onSubmitSuccess?.({
          id: response.data.id,
          name: response.data.name,
          acceptable_terms: response.data.acceptable_terms,
          unacceptable_terms: response.data.unacceptable_terms,
        });
        onRefreshNeeded?.();
        onClose();
      } else {
        setError(response.error || 'Erreur lors de la sauvegarde');
        logger.error('Erreur sauvegarde clause:', response.error);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      logger.error('Erreur sauvegarde clause:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, clauseId, formData, versions, activeVersions, onClose, onSubmitSuccess, onRefreshNeeded]);

  const title = mode === 'add' ? 'Ajouter une clause' : 'Modifier la clause';

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      submitDisabled={!formData.name.trim() || activeVersions.length === 0}
    >
      {error && (
        <div className="form-error-message">
          {error}
        </div>
      )}

      <div className="form-field">
        <label htmlFor="clause-name" className="form-field-label">
          Nom de la clause *
        </label>
        <input
          id="clause-name"
          type="text"
          className="form-field-input"
          value={formData.name}
          onChange={(e) => updateField('name', e.target.value)}
          placeholder="Ex: Clause de confidentialité"
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="form-field">
        <label className="form-field-label">
          Termes préférables
        </label>
        <RichTextEditor
          value={formData.acceptable_terms}
          onChange={(html) => updateField('acceptable_terms', html)}
          placeholder="Décrivez les termes préférables pour cette clause..."
        />
      </div>

      <div className="form-field">
        <label className="form-field-label">
          Termes inacceptables
        </label>
        <RichTextEditor
          value={formData.unacceptable_terms}
          onChange={(html) => updateField('unacceptable_terms', html)}
          placeholder="Décrivez les termes inacceptables pour cette clause..."
        />
      </div>

      {/* Versions section */}
      <div className="form-field clause-versions-section">
        <div className="clause-versions-header">
          <label className="form-field-label">
            Versions *
          </label>
          <button
            type="button"
            className="clause-version-add-btn"
            onClick={handleAddVersion}
            disabled={isSubmitting}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Ajouter une version
          </button>
        </div>

        {activeVersions.length > 0 ? (
          <ul className="clause-versions-list">
            {activeVersions.map(version => (
              <li key={version.tempId} className="clause-version-item">
                <span className="clause-version-name">{version.name}</span>
                <div className="clause-version-actions">
                  <button
                    type="button"
                    className="clause-version-edit-btn"
                    onClick={() => handleEditVersion(version)}
                    disabled={isSubmitting}
                    title="Modifier cette version"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="clause-version-delete-btn"
                    onClick={() => handleDeleteVersion(version.tempId)}
                    disabled={isSubmitting}
                    title="Retirer cette version"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18"></path>
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="clause-versions-empty">
            <p>Aucune version ajoutée. Cliquez sur "Ajouter une version" pour commencer.</p>
          </div>
        )}
      </div>

      {/* Embedded ClauseVersionModal */}
      <ClauseVersionModal
        isOpen={versionModalOpen}
        onClose={handleVersionModalClose}
        mode={versionModalMode}
        embeddedMode={true}
        defaultName={activeVersions.length === 0 ? 'Standard' : undefined}
        initialData={editingVersion ? {
          name: editingVersion.name,
          content: editingVersion.content,
          selection_criteria: editingVersion.selection_criteria,
        } : undefined}
        onSaveLocal={handleVersionSaveLocal}
      />
    </FormModal>
  );
};
